"use client";

import { Check, House } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import { cn } from "@/lib/_utils";
import { EXPERT_CATEGORIES } from "./data";

type CategoryId = (typeof EXPERT_CATEGORIES)[number]["id"];

export function HomeV2ExpertCategories() {
  const [activeId, setActiveId] = useState<CategoryId>(EXPERT_CATEGORIES[0].id);
  const active =
    EXPERT_CATEGORIES.find((c) => c.id === activeId) ?? EXPERT_CATEGORIES[0];

  return (
    <section className="bg-white px-4 py-20 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-[1400px] flex-col items-center">
        <div className="mx-auto max-w-[1056px] text-center">
          <h2 className="font-bold text-[#2C2C2C] text-[56px] leading-[1.1] tracking-[-0.02em] max-lg:text-4xl">
            Find the right expert
          </h2>
          <div aria-hidden className="h-6" />
          <p className="mx-auto max-w-[989px] font-normal text-[#3F474E] text-xl leading-[1.4]">
            Receive professional guidance and assistance for your home projects
            with Teletradie. From home repairs to interior design, we have all
            your needs covered!
          </p>
        </div>

        <div aria-hidden className="h-12" />

        {/* Tabs — #E9EDED track, pill active */}
        <div className="w-full max-w-[976px] overflow-x-auto">
          <div className="mx-auto flex min-w-max items-center gap-6 rounded-[30px] bg-[#E9EDED] p-2">
            {EXPERT_CATEGORIES.map((category) => (
              <button
                className={cn(
                  "flex h-[45px] min-w-[130px] cursor-pointer items-center justify-center rounded-full px-6 text-lg leading-[1.4] transition-colors",
                  activeId === category.id
                    ? "bg-white font-bold text-[#2C2C2C] shadow-sm"
                    : "font-semibold text-[#2C2C2C] hover:bg-white/50"
                )}
                key={category.id}
                onClick={() => setActiveId(category.id)}
                type="button"
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        <div aria-hidden className="h-10" />

        {/* Content card — gradient, 40px radius, 80px gap */}
        <div className="tt-gradient flex w-full flex-col overflow-hidden rounded-[40px] border border-[#BCCAD4] lg:min-h-[620px] lg:flex-row lg:items-stretch lg:gap-20 lg:py-12 lg:pr-0 lg:pl-20">
          <div className="flex min-w-0 flex-1 flex-col px-6 py-10 sm:px-10 lg:max-w-[620px] lg:px-0 lg:py-0">
            <div aria-hidden className="hidden h-12 lg:block" />

            <House className="size-16 text-white" strokeWidth={1.25} />

            <div aria-hidden className="h-4" />

            <h3 className="font-bold text-[40px] text-white leading-[1.2] max-sm:text-3xl">
              {active.title}
            </h3>

            <div aria-hidden className="h-6" />

            <p className="font-normal text-[#F5F5F5] text-xl leading-[1.4]">
              {active.description}
            </p>

            <div aria-hidden className="h-10" />

            <ul className="flex flex-col gap-5">
              {active.features.map((feature) => (
                <li className="flex items-start gap-4" key={feature}>
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[#02A8EA]/25">
                    <Check className="size-4 stroke-[3] text-[#02A8EA]" />
                  </span>
                  <span className="font-normal text-[#F5F5F5] text-xl leading-[1.4]">
                    {feature}
                  </span>
                </li>
              ))}
            </ul>

            <div aria-hidden className="hidden h-12 lg:block" />
          </div>

          <div className="relative min-h-[280px] w-full overflow-hidden rounded-t-[30px] bg-[#F5F5F5] sm:min-h-[360px] lg:min-h-0 lg:w-[620px] lg:shrink-0 lg:rounded-none lg:rounded-l-[30px]">
            <Image
              alt={active.imageAlt}
              className="object-cover"
              fill
              sizes="(max-width: 1024px) 100vw, 620px"
              src={active.image}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
